import {UserHomepageComponent, UserHomepageComponentTypes, UserHomepageContent, UserHomePageStateData, UserHomepageTab} from "../models";
import DateUtils from "../../../../core/services/date-utils";
import {deepEqual} from "../../../../core/services/utils/utils";

class UserHomepageUtils {

    /**
     * Resolves the deal finish datetime for time-based components.
     * Prefers content.finishDateTime, then falls back to endDate.
     */
    static getTimeBasedFinishDateTime(component?: UserHomepageComponent): string | undefined {
        return component?.content?.finishDateTime ?? component?.endDate;
    }

    /**
     * Maps a time-based component to the updateContent API shape.
     */
    static getTimeBasedContentForApi(component?: UserHomepageComponent) {
        return {
            partIds: this.getComponentContentList(component)?.map((content) => content.id) ?? [],
            endtimeDateTime: this.getTimeBasedFinishDateTime(component) ?? '',
        };
    }

    /**
     * Fetches the list of the component content based on the type of the component.
     *
     * @param {UserHomepageComponent} component
     * @return {UserHomepageContent}
     */
    static getComponentContentList(component?: UserHomepageComponent): UserHomepageContent[] {
        let list: UserHomepageContent[] = [];
        switch (component?.type?.id) {
            case UserHomepageComponentTypes.slideShow:
            case UserHomepageComponentTypes.banner:
                list = component?.content?.banners ?? [];
                break;
            case UserHomepageComponentTypes.imageCarousel:
                list = component?.content?.images ?? [];
                break;
            case UserHomepageComponentTypes.category:
                list = component?.content?.categories ?? [];
                break;
            case UserHomepageComponentTypes.smallProduct:
            case UserHomepageComponentTypes.mediumProduct:
            case UserHomepageComponentTypes.largeProduct:
            case UserHomepageComponentTypes.timeBased:
                list = component?.content?.parts ?? [];
                break;
            default:
                break;
        }
        return list;
    }

    /**
     * Updates the contentList of the given component with the given contentList and return the updated component.
     *
     * @param {UserHomepageComponent?} component
     * @param {UserHomepageContent[]?} contentList
     * @return {UserHomepageComponent | undefined}
     */
    static updateComponentContentList(component?: UserHomepageComponent, contentList?: UserHomepageContent[]): UserHomepageComponent | undefined {
        switch (component?.type?.id) {
            case UserHomepageComponentTypes.slideShow:
            case UserHomepageComponentTypes.banner:
                return {
                    ...component,
                    content: {
                        banners: contentList ?? [],
                    },
                }
            case UserHomepageComponentTypes.imageCarousel:
                return {
                    ...component,
                    content: {
                        ...(component.content ?? {}),
                        images: contentList ?? [],
                    },
                }
            case UserHomepageComponentTypes.category:
                return {
                    ...component,
                    content: {
                        categories: contentList ?? [],
                    },
                }
            case UserHomepageComponentTypes.smallProduct:
            case UserHomepageComponentTypes.mediumProduct:
            case UserHomepageComponentTypes.largeProduct:
                return {
                    ...component,
                    content: {
                        parts: contentList ?? [],
                    },
                }
            case UserHomepageComponentTypes.timeBased:
                return {
                    ...component,
                    content: {
                        ...(component.content ?? {}),
                        parts: contentList ?? [],
                    },
                }
            default:
                return component;
        }
    }

    /**
     * Removes the given content from the list of content in the component.
     *
     * @param {UserHomepageComponent?} component
     * @param {number?} contentId
     * @return {UserHomepageComponent | undefined}
     */
    static removeContentFromComponentContentList(component?: UserHomepageComponent, contentId?: number): UserHomepageComponent | undefined {
        switch (component?.type?.id) {
            case UserHomepageComponentTypes.slideShow:
            case UserHomepageComponentTypes.banner:
                return {
                    ...component,
                    content: {
                        banners: component.content?.banners?.filter(e => e.id !== contentId) ?? [],
                    },
                }
            case UserHomepageComponentTypes.imageCarousel:
                return {
                    ...component,
                    content: {
                        ...(component.content ?? {}),
                        images: component.content?.images?.filter(e => e.id !== contentId) ?? [],
                    },
                }
            case UserHomepageComponentTypes.category:
                return {
                    ...component,
                    content: {
                        categories: component.content?.categories?.filter(e => e.id !== contentId) ?? [],
                    },
                }
            case UserHomepageComponentTypes.smallProduct:
            case UserHomepageComponentTypes.mediumProduct:
            case UserHomepageComponentTypes.largeProduct:
                return {
                    ...component,
                    content: {
                        parts: component.content?.parts?.filter(e => e.id !== contentId) ?? [],
                    },
                }
            case UserHomepageComponentTypes.timeBased:
                return {
                    ...component,
                    content: {
                        ...(component.content ?? {}),
                        parts: component.content?.parts?.filter(e => e.id !== contentId) ?? [],
                    },
                }
            default:
                return component;
        }
    }

    /**
     * Adds the given content to the list of content in the component.
     *
     * @param {UserHomepageComponent?} component
     * @param {UserHomepageContent} content
     * @return {UserHomepageComponent | undefined}
     */
    static appendContentToComponentContentList(component: UserHomepageComponent, content: UserHomepageContent): UserHomepageComponent | undefined {
        switch (component?.type?.id) {
            case UserHomepageComponentTypes.slideShow:
            case UserHomepageComponentTypes.banner:
                return {
                    ...component,
                    content: {
                        banners: [
                            ...(component.content?.banners ?? []),
                            content,
                        ],
                    },
                }
            case UserHomepageComponentTypes.imageCarousel:
                return {
                    ...component,
                    content: {
                        ...(component.content ?? {}),
                        images: [
                            ...(component.content?.images ?? []),
                            content,
                        ],
                    },
                }
            case UserHomepageComponentTypes.category:
                return {
                    ...component,
                    content: {
                        categories: [
                            ...(component.content?.categories ?? []),
                            content,
                        ],
                    },
                }
            case UserHomepageComponentTypes.smallProduct:
            case UserHomepageComponentTypes.mediumProduct:
            case UserHomepageComponentTypes.largeProduct:
                return {
                    ...component,
                    content: {
                        parts: [
                            ...(component.content?.parts ?? []),
                            content,
                        ]
                    },
                }
            case UserHomepageComponentTypes.timeBased:
                return {
                    ...component,
                    content: {
                        ...(component.content ?? {}),
                        parts: [
                            ...(component.content?.parts ?? []),
                            content,
                        ],
                    },
                }
            default:
                return component;
        }
    }

    /**
     * Adds or removes the given tag from the content list of the following component.
     *
     * @param {UserHomepageComponent?} component
     * @param {UserHomepageContent?} content
     * @return {UserHomepageComponent | undefined}
     */
    static updateContentInComponentContentList(component: UserHomepageComponent, content?: UserHomepageContent): UserHomepageComponent | undefined {
        return {
            ...component,
            content: {
                banners: component.content?.banners?.map(e => content?.id === e.id
                    ? {
                        ...e,
                        ...content
                    }
                    : e
                ) ?? [],
                parts: component.content?.parts?.map(e => content?.id === e.id
                    ? {
                        ...e,
                        ...content
                    }
                    : e
                ) ?? [],
                categories: component.content?.categories?.map(e => content?.id === e.id
                    ? {
                        ...e,
                        ...content
                    }
                    : e
                ) ?? [],
                images: component.content?.images?.map(e => content?.id === e.id
                    ? {
                        ...e,
                        ...content
                    }
                    : e
                ) ?? [],
            },
        }
    }

    /**
     * Finds the content associated with the given contentId from the current component.
     *
     * @param {UserHomepageComponent} component
     * @param {number} contentId
     * @return {UserHomepageContent | undefined}
     */
    static getComponentContent(component: UserHomepageComponent, contentId: number): UserHomepageContent | undefined {
        return this.getComponentContentList(component).find(e => e.id === contentId);
    }

    /**
     * Normalizes a carousel image entity for control-panel state.
     */
    static prepareCarouselImageContent(data?: Partial<UserHomepageContent>, orderIndex?: number): UserHomepageContent {
        return {
            id: data?.id,
            orderIndex: data?.orderIndex ?? orderIndex,
            fileGuid: data?.fileGuid,
            fileExtension: data?.fileExtension,
            tags: data?.tags?.map((tag: any) => ({
                id: tag.id,
                name: tag.name,
                industryId: tag.industryId,
                isPublic: tag.isPublic,
                iconFileName: tag.iconInfo?.url ?? tag.iconFileName ?? null,
            })) ?? [],
        } as UserHomepageContent;
    }

    /**
     * Assigns a temporary negative id for carousel images that have not been saved yet.
     */
    static getTempCarouselImageId(images: UserHomepageContent[] = []): number {
        const tempIds = images
            .map((image) => image.id)
            .filter((id): id is number => typeof id === 'number' && id < 0);
        return tempIds.length ? Math.min(...tempIds) - 1 : -1;
    }

    /**
     * Maps carousel images to the updateContent API shape.
     */
    static getImageCarouselContentForApi(component?: UserHomepageComponent) {
        return {
            images: (component?.content?.images ?? []).map((image) => ({
                fileGuid: image?.fileGuid,
                fileExtension: image?.fileExtension,
                tagIds: image?.tags?.map((tag) => tag.id) ?? [],
            })),
            imageWidth: component?.content?.imageWidth ?? null,
            imageHeight: component?.content?.imageHeight ?? null,
        };
    }

    /**
     * Builds the content portion of updateContent for the given component changes.
     */
    static getComponentContentChanges(
        prev?: UserHomepageComponent,
        curr?: UserHomepageComponent,
    ): Record<string, any> | undefined {
        const typeId = curr?.type?.id;
        if (!typeId) {
            return undefined;
        }

        if (typeId === UserHomepageComponentTypes.imageCarousel) {
            if (deepEqual(
                this.getImageCarouselContentForApi(prev),
                this.getImageCarouselContentForApi(curr),
            )) {
                return undefined;
            }
            return {
                imageCarousel: this.getImageCarouselContentForApi(curr),
            };
        }

        if (typeId === UserHomepageComponentTypes.timeBased) {
            const prevPayload = this.getTimeBasedContentForApi(prev);
            const currPayload = this.getTimeBasedContentForApi(curr);
            if (deepEqual(prevPayload, currPayload)) {
                return undefined;
            }
            return {timeBasedParts: currPayload};
        }

        const prevContentIds = this.getComponentContentList(prev)?.map((content) => content.id) ?? [];
        const currContentIds = this.getComponentContentList(curr)?.map((content) => content.id) ?? [];

        if (deepEqual(prevContentIds, currContentIds)) {
            return undefined;
        }

        switch (typeId) {
            case UserHomepageComponentTypes.smallProduct:
            case UserHomepageComponentTypes.mediumProduct:
            case UserHomepageComponentTypes.largeProduct:
                return {parts: {partIds: currContentIds}};
            case UserHomepageComponentTypes.category:
                return {categories: {categoryIds: currContentIds}};
            case UserHomepageComponentTypes.banner:
                return {banner: {bannerId: currContentIds[0]}};
            case UserHomepageComponentTypes.slideShow:
                return {banners: {bannerIds: currContentIds}};
            default:
                return undefined;
        }
    }

    /**
     * Fetches the preview-able components.
     * @param {} components
     */
    static getPreviewComponents(components?: UserHomepageComponent[]): UserHomepageComponent[] {
        return components?.filter((component) => {
                const finishDateTime = component.type?.id === UserHomepageComponentTypes.timeBased
                    ? this.getTimeBasedFinishDateTime(component)
                    : component.endDate;
                // expired
                if (finishDateTime && DateUtils.server(finishDateTime).isBefore(DateUtils.serverNow())) {
                    return false;
                }
                // not yet started
                if (component.startDate && DateUtils.server(component.startDate).isAfter(DateUtils.serverNow())) {
                    return false;
                }
                //  no content
                if (!Object.keys(component?.content ?? {})?.length) {
                    return false;
                }
                // no entry for any of the content property lists (i.e. empty)
                const contentList = this.getComponentContentList(component);
                return contentList?.length;
            })
            ?? []
    }

    /**
     * Get the tab associated from the given tabId.
     * @param {UserHomePageStateData} data
     * @param {number} tabId
     */
    static getSelection(data?: UserHomePageStateData, tabId?: number)
        : UserHomepageTab | undefined;
    /**
     * Get the component associated from the given componentId that also belongs to the tab associated to the tabId.
     * @param {UserHomePageStateData} data
     * @param {number} tabId
     * @param {number} componentId
     */
    static getSelection(data?: UserHomePageStateData, tabId?: number, componentId?: number)
        : UserHomepageComponent | undefined;
    /**
     * Get the content associated with the given contentId that also belongs to the component associated from the given
     * componentId that also belongs to the tab associated to the tabId.
     * @param {UserHomePageStateData} data
     * @param {number} tabId
     * @param {number} componentId
     * @param {number} contentId
     */
    static getSelection(data?: UserHomePageStateData, tabId?: number, componentId?: number, contentId?: number)
        : UserHomepageContent | undefined;
    /**
     * Get most appropriate selection based on the given arguments.
     *
     * @param {UserHomePageStateData} data
     * @param {number} tabId
     * @param {number} componentId
     * @param {number} contentId
     */
    static getSelection(data?: UserHomePageStateData, tabId?: number, componentId?: number, contentId?: number)
        : UserHomepageTab | UserHomepageComponent | UserHomepageContent | undefined {
        if (!tabId)
            return undefined;

        const tab = data?.tabs?.find(e => e.id === tabId);
        if (!componentId)
            return tab;

        const component = tab?.components?.find(e => e.id === componentId);
        if (!contentId)
            return component;

        if (component)
            return UserHomepageUtils.getComponentContent(component, contentId);
    }
}

export default UserHomepageUtils;
